import React from 'react'
import 'project/polyfill'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import Strings from 'project/localisation'
import CommunutyGoalsCard from 'components/cards/CommunityGoalsCard'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import { outreachStrings } from 'common/strings/outreach-strings'
import { businessSupportStrings } from 'common/strings/business-support-strings'
import FullScreenHero from 'components/hero/FullScreenHero'
import useWindowSize from 'components/util/useWindowSize'
import OtherCommunityPages from 'components/company/OtherCommunityPages'
import VisionColumns from 'components/company/VisionColumns'
import VisionImages from 'components/company/VisionImages'
import classNames from 'classnames'
import OurPartners from 'components/company/OurPartners'
import TalkToUs from 'components/company/TalkToUs'
import imageResize from 'project/image-proxy-web'

export type HomePageType = {}
const HomePage: NextPageWithLayout<HomePageType> = () => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description: outreachStrings.seoDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/outreach/',
    },
    title: outreachStrings.seoTitle,
  }
  const breadcrumb = homeBreadcrumb().concat([
    {
      item: 'https://www.hoxtonmix.com/outreach/',
      name: `${Strings.outreach}`,
      position: 2,
    },
  ])
  const size = useWindowSize()
  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <FullScreenHero
        className={'hero--outreach'}
        title={Strings.outreach}
        subtitle={`${outreachStrings.businessAndEmployment}`}
        backgroundImage={imageResize(
          require('public/images/hero/outreach-desktop.jpg'),
        )}
        option={3}
      />
      <main className={'overflow-hidden'}>
        <CommunutyGoalsCard
          heroTitle={`${Strings.outreach}`}
          heroSubtitle={`${outreachStrings.businessAndEmployment}`}
          title={
            size.isLgOrHigher ? `${Strings.outreach}` : `${Strings.support}`
          }
          description={outreachStrings.description}
          option={3}
        />
        <div
          className={classNames(
            size.isLgOrHigher ? 'bg-gradient-pink2' : 'bg-gradient-pink3',
            'pt-xxl-52 pt-lg-21 pt-12 pb-xxl-26 pb-lg-15 pb-8',
          )}
        >
          <div className='container'>
            <VisionColumns
              columnsClasses={
                'col-xxxl-11 col-xxl-8 col-lg-10 pe-xxl-0 pe-lg-18'
              }
              category={businessSupportStrings.support}
              title={outreachStrings.businessLifecycle}
              titleClasses={'pe-lg-0 pe-6'}
              text={outreachStrings.businessLifecycleContent}
              textClasses={
                'pe-xxxl-60 pe-xxl-80 pe-xl-72 pe-lg-42 pe-4 text-semibold'
              }
            />
          </div>
          <div className='container-lg px-lg-4 px-0'>
            <VisionImages
              images={[
                {
                  alt: 'Support',
                  className: 'd-block mt-lg-33 mt-6 position-relative',
                  src: require('public/images/company/outreach-1.png'),
                  width: size.isLgOrHigher ? '47%' : '81%',
                },
                {
                  alt: 'Support',
                  className: 'd-block mt-lg-n44 mt-n8 ms-auto me-0',
                  src: require('public/images/company/outreach-2.png'),
                  width: size.isLgOrHigher ? '65%' : '90%',
                },
              ]}
            />
          </div>
          <div className={'container mt-xxl-54 mt-lg-21 mt-12'}>
            <VisionColumns
              columnsClasses={
                'col-xxxl-11 col-xxl-8 col-lg-10 pe-xxl-0 pe-lg-18'
              }
              category={businessSupportStrings.bursaryProgram}
              title={outreachStrings.ourEventsCreateOpportunities}
              titleClasses={'pe-0'}
              text={outreachStrings.ourEventsCreateOpportunitiesContent}
              textClasses={'pe-xxxl-58 pe-xxl-84 pe-xl-72 pe-lg-42 pe-8'}
            />
            <VisionColumns
              className={'mt-lg-27 mt-5'}
              columnsClasses={
                'col-xxxl-11 col-xxl-8 col-lg-10 pe-xxl-0 pe-lg-18'
              }
              title={outreachStrings.weEnjoyGreatRelationships}
              titleClasses={'pe-xxl-12'}
              text={outreachStrings.weEnjoyGoodRelationshipsDescriptions}
              textClasses={'pe-xxxl-58 pe-xxl-80 pe-xl-72 pe-lg-38 pe-8'}
            />
          </div>
        </div>
        <div className={'bg-gradient-blue3 pb-xxl-52 pb-lg-15 pb-8'}>
          <div className='container py-xxl-50 py-xl-38 py-lg-12 py-12'>
            <h2
              className={classNames(
                'text-center text-bold mb-6',
                size.isLgOrHigher ? 'fs-fluid-36-80 lh-105' : 'h3',
              )}
            >
              {outreachStrings.hackadramHighlights}
            </h2>
            <p
              className={classNames(
                'text-center text-lg-semibold mb-0',
                !size.isSmOrLower
                  ? 'h4 h4--large'
                  : 'lead lead--ls-lg-on-mobile px-2',
              )}
              dangerouslySetInnerHTML={{
                __html: outreachStrings.hackadramHighlightsDescription,
              }}
            />
          </div>
          <div className='vimeo'>
            <iframe
              className='video'
              src='https://player.vimeo.com/video/272929771?title=0&amp;byline=0&amp;portrait=0'
              frameBorder='0'
              allow='autoplay; fullscreen'
              allowFullScreen
              data-ready='true'
            ></iframe>
          </div>
          <div className='container py-xxl-50 py-xl-38 py-lg-12 py-12'>
            <h2
              className={classNames(
                'text-center text-bold mb-6',
                size.isLgOrHigher ? 'fs-fluid-36-80 lh-105' : 'h3 px-6',
              )}
            >
              {outreachStrings.rehack}
            </h2>
            <p
              className={classNames(
                'text-center text-lg-semibold mb-0',
                !size.isSmOrLower
                  ? 'h4 h4--large'
                  : 'lead lead--ls-lg-on-mobile',
              )}
              dangerouslySetInnerHTML={{
                __html: outreachStrings.rehackDescription,
              }}
            />
          </div>
          <div className='vimeo'>
            <iframe
              className='video'
              src='https://player.vimeo.com/video/272929771?title=0&amp;byline=0&amp;portrait=0'
              frameBorder='0'
              allow='autoplay; fullscreen'
              allowFullScreen
              data-ready='true'
            ></iframe>
          </div>
        </div>
        <OurPartners
          className={'mt-xxl-50 mt-lg-21 mt-14 mb-xxxl-58 mb-lg-66 mb-60'}
        />
        <div
          className={classNames(
            size.isLgOrHigher ? 'bg-gradient-pink2' : 'bg-gradient-pink3',
            'pt-xxxl-84 pt-lg-45 pt-56 position-relative',
          )}
        >
          <div
            className={
              'container-lg px-lg-3 px-0 d-lg-flex justify-content-between align-items-center position-absolute start-0 end-0 top-n6p'
            }
          >
            <img
              loading='lazy'
              className='d-lg-inline d-block'
              alt={'Innovating'}
              style={{
                height: 'auto',
                width: size.isLgOrHigher ? '35%' : '262px',
              }}
              src={imageResize(
                require('public/images/company/innovating-3.png'),
              )}
            />
            <img
              loading='lazy'
              className={classNames(
                'd-block mt-lg-0 mt-n8 mx-lg-0 ms-auto me-0',
              )}
              alt={'Innovating'}
              style={{
                height: 'fit-content',
                width: size.isLgOrHigher ? '30%' : '232px',
              }}
              src={imageResize(
                require('public/images/company/innovating-4.png'),
              )}
            />
          </div>
          <div className='container'>
            <p
              className={'h4 h4--small text-center text-bold mb-lg-3 mb-3'}
              dangerouslySetInnerHTML={{
                __html: businessSupportStrings.helpingStartups,
              }}
            />
            <h2
              className={classNames(
                'text-center text-bold',
                size.isLgOrHigher ? 'fs-fluid-36-80 lh-105' : 'h3',
              )}
            >
              {businessSupportStrings.alwaysInnovating}
            </h2>
          </div>
          <div className='container-lg px-lg-4 px-0 d-lg-flex justify-content-between mt-lg-21 mt-16 mb-lg-22'>
            <VisionImages
              images={[
                {
                  alt: 'Innovating',
                  className: 'mt-lg-18 ms-lg-30',
                  height: 'fit-content',
                  src: require('public/images/company/innovating-5.png'),
                  width: !size.isSmOrLower ? '47%' : '84%',
                },
                {
                  alt: 'Innovating',
                  className: 'd-block ms-auto me-lg-20 me-0 mt-lg-0 mt-5',
                  src: require('public/images/company/innovating-6.png'),
                  width: !size.isSmOrLower ? '27%' : '77%',
                },
              ]}
            />
          </div>
          <img
            loading='lazy'
            src={imageResize('/images/company/innovating-7.png')}
            alt={'Innovating'}
            className={'img-fluid mt-lg-0 mt-6 transform-scale-sm-1.5'}
          />
          <div className='pb-xxl-50 pb-lg-21 pb-14'>
            <div className='container-lg px-lg-4 px-0 position-relative mt-sm-n42 mt-14'>
              <VisionImages
                images={[
                  {
                    alt: 'Innovating',
                    className: 'img-fluid d-block mx-auto',
                    height: 'fit-content',
                    src: require('public/images/company/innovating-8.png'),
                    width: !size.isSmOrLower ? '66%' : '257px',
                  },
                ]}
              />
            </div>
            <TalkToUs />
          </div>
        </div>
        <OtherCommunityPages
          items={[
            {
              slug: 'social-impact',
              title: 'Social Impact',
            },
            {
              slug: 'business-support',
              title: 'Business Support',
            },
          ]}
        />
        <div className={'spacer'} />
      </main>
    </SeoContainer>
  )
}

export default HomePage
